// src/Views/Admin/AdminPresence.jsx
import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../../firebase';
import './AdminPresence.css';

const AdminPresence = () => {
  const [presenceData, setPresenceData] = useState([]);
  const [stats, setStats] = useState({
    online: 0,
    lastHour: 0,
    total: 0
  });

  useEffect(() => {
    const presenceRef = ref(rtdb, 'presence');

    const unsubscribe = onValue(presenceRef, (snapshot) => {
      if (!snapshot.exists()) {
        setPresenceData([]);
        setStats({ online: 0, lastHour: 0, total: 0 });
        return;
      }

      const data = snapshot.val();
      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000);

      const users = Object.entries(data).map(([uid, info]) => ({
        uid,
        email: info.email || 'Unknown',
        online: info.online || false,
        lastSeen: info.lastSeen || 0
      }));

      // Sort: online first, then by lastSeen
      users.sort((a, b) => {
        if (a.online && !b.online) return -1;
        if (!a.online && b.online) return 1;
        return b.lastSeen - a.lastSeen;
      });

      const onlineCount = users.filter(u => u.online).length;
      const lastHourCount = users.filter(u => u.online || u.lastSeen > oneHourAgo).length;

      setPresenceData(users);
      setStats({
        online: onlineCount,
        lastHour: lastHourCount,
        total: users.length
      });
    });

    return () => unsubscribe();
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="admin-presence">
      <h1>👥 User Presence Monitor</h1>
      
      {/* Stats Cards */}
      <div className="presence-stats">
        <div className="stat-card online">
          <div className="stat-number">{stats.online}</div>
          <div className="stat-label">Online Now</div>
          <div className="stat-indicator pulse"></div>
        </div>
        
        <div className="stat-card hour">
          <div className="stat-number">{stats.lastHour}</div>
          <div className="stat-label">Active (Last Hour)</div>
        </div>
        
        <div className="stat-card total">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Tracked</div>
        </div>
      </div>

      {/* Users Table */}
      <div className="presence-table-container">
        <table className="presence-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Email</th>
              <th>Last Seen</th>
              <th>Time Ago</th>
            </tr>
          </thead>
          <tbody>
            {presenceData.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  No presence data yet. Users will appear here once they log in.
                </td>
              </tr>
            ) : (
              presenceData.map((user) => (
                <tr key={user.uid} className={user.online ? 'user-online' : 'user-offline'}>
                  <td>
                    <span className={`status-dot ${user.online ? 'online' : 'offline'}`}></span>
                    {user.online ? 'Online' : 'Offline'}
                  </td>
                  <td>{user.email}</td>
                  <td>{formatTime(user.lastSeen)}</td>
                  <td>{user.online ? 'Now' : getTimeSince(user.lastSeen)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPresence;